import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDevice } from "~/lib/auth/device-context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { earnPoints, redeemPoints } from "~/lib/api/api-client";
import { queueTransaction } from "~/lib/offline/offline-queue.client";
import { shouldQueue } from "~/lib/offline/should-queue";

import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Divider,
    Alert,
    AlertTitle,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    AddCircleOutline,
    RemoveCircleOutline,
    ArrowForward,
    ArrowBack,
    AttachMoney,
    Star,
} from "@mui/icons-material";

interface TransactionFormProps {
    customer: {
        barcode: string;
        pointsBalance: number;
    };
    onSuccess: () => void;
    initialTransactionType?: "EARN" | "REDEEM_DIRECT";
    hideTypeSelector?: boolean; // Hide the type selector buttons when on dedicated pages
}

export function TransactionForm({
    customer,
    onSuccess,
    initialTransactionType = "EARN",
    hideTypeSelector = false,
}: TransactionFormProps) {
    const [transactionType, setTransactionType] = useState<"EARN" | "REDEEM_DIRECT">(
        initialTransactionType
    );
    const [amount, setAmount] = useState("");
    const [points, setPoints] = useState("");
    const [redeemMode, setRedeemMode] = useState<"direct" | "return">("direct");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationTimer, setConfirmationTimer] = useState(60); // 60 second countdown
    const [redirectTimer, setRedirectTimer] = useState<number | null>(null); // null = not redirecting
    const [isOnline, setIsOnline] = useState(
        typeof window !== "undefined" ? navigator.onLine : true
    );

    const { isRegistered, deviceToken, cashierName } = useDevice();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Monitor online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Confirmation dialog auto-confirm timer (60 seconds)
    useEffect(() => {
        if (!showConfirmation) {
            setConfirmationTimer(60); // Reset when dialog closes
            return;
        }

        if (confirmationTimer <= 0) {
            // Timer expired - auto-confirm
            handleConfirm();
            return;
        }

        const timer = setTimeout(() => {
            setConfirmationTimer(confirmationTimer - 1);
        }, 1000);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showConfirmation, confirmationTimer]); // Don't include handleConfirm to avoid infinite loop

    // Post-transaction redirect timer (3 seconds)
    useEffect(() => {
        if (redirectTimer === null) return;

        if (redirectTimer <= 0) {
            // Timer expired - force redirect
            console.log('[TransactionForm] Redirect timer expired, navigating to home');
            navigate("/");
            return;
        }

        const timer = setTimeout(() => {
            setRedirectTimer(redirectTimer - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [redirectTimer, navigate]);

    const earnMutation = useMutation({
        mutationFn: async ({ barcode, amount }: { barcode: string; amount: number }) => {
            if (!deviceToken || !cashierName)
                throw new Error("الرجاء تسجيل الجهاز أولاً");
            try {
                console.log("[TransactionForm] earnPoints() calling fetch", { barcode, amount });
                const result = await earnPoints({
                    barcode,
                    amount,
                    cashier_name: cashierName,
                    deviceToken,
                });
                console.log("[TransactionForm] earnPoints() returned", { result });
                if (!result.success) throw new Error(result.error || "حدث خطأ أثناء تسجيل المعاملة");
                return result.transaction;
            } catch (err: any) {
                console.log("[TransactionForm] earnPoints threw", {
                    errName: err?.name,
                    errMessage: err?.message,
                    isOnline,
                    navigatorOnLine: typeof navigator !== "undefined" ? navigator.onLine : null,
                });
                if (shouldQueue(err, isOnline)) {
                    console.log("[TransactionForm] shouldQueue=true, writing to IndexedDB queue");
                    try {
                        await queueTransaction({
                            type: "EARN",
                            customer_barcode: barcode,
                            amount,
                            device_token: deviceToken!,
                            cashier_name: cashierName ?? undefined,
                        });
                        console.log("[TransactionForm] queueTransaction() returned OK");
                    } catch (qErr: any) {
                        console.error("[TransactionForm] queueTransaction() THREW", qErr);
                    }
                    throw new Error("غير متصل – تم حفظ العملية للمزامنة لاحقًا");
                }
                console.log("[TransactionForm] shouldQueue=false, re-throwing original error");
                throw err;
            }
        },
        onSuccess: () => {
            handleTransactionSuccess(`تم إضافة ${amount} جنيه بنجاح`);
        },
        onError: (error: Error) => {
            setError(error.message || "حدث خطأ أثناء الاتصال بالخادم");
            setShowConfirmation(false);
        },
    });

    const redeemMutation = useMutation({
        mutationFn: async ({ barcode, points_to_redeem }: { barcode: string; points_to_redeem: number }) => {
            if (!deviceToken || !cashierName)
                throw new Error("الرجاء تسجيل الجهاز أولاً");
            try {
                const result = await redeemPoints({
                    barcode,
                    points_to_redeem,
                    cashier_name: cashierName,
                    deviceToken,
                });
                if (!result.success) throw new Error(result.error || "حدث خطأ أثناء تسجيل المعاملة");
                return result.transaction;
            } catch (err: any) {
                if (shouldQueue(err, isOnline)) {
                    await queueTransaction({
                        type: "REDEEM_DIRECT",
                        redeem_mode: redeemMode === "return" ? "return" : "normal",
                        customer_barcode: barcode,
                        points: points_to_redeem,
                        device_token: deviceToken!,
                        cashier_name: cashierName ?? undefined,
                    });
                    throw new Error("غير متصل – تم حفظ العملية للمزامنة لاحقًا");
                }
                throw err;
            }
        },
        onSuccess: () => {
            const msg =
                redeemMode === "return"
                    ? `تم إضافة ${points} نقطة (إرجاع)`
                    : `تم خصم ${points} نقطة`;
            handleTransactionSuccess(msg);
        },
        onError: (error: Error) => {
            setError(error.message || "حدث خطأ أثناء الاتصال بالخادم");
            setShowConfirmation(false);
        },
    });

    const handleTransactionSuccess = (msg: string) => {
        console.log('[TransactionForm] Transaction success, starting redirect timer');
        queryClient.invalidateQueries({ queryKey: ["customer"] });
        setSuccessMessage(msg);
        setPoints("");
        setAmount("");
        setShowConfirmation(false);
        setError(""); // Clear any previous errors
        onSuccess();
        // Start redirect countdown timer (3 seconds)
        setRedirectTimer(3);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!isRegistered) {
            setError("الرجاء تسجيل الجهاز أولاً");
            return;
        }
        if (transactionType === "EARN") {
            const val = parseFloat(amount);
            if (!val || val <= 0) {
                setError("الرجاء إدخال مبلغ صحيح");
                return;
            }
            setShowConfirmation(true);
        } else {
            if (redeemMode === "direct") {
                const val = parseFloat(points);
                if (!val || val <= 0) {
                    setError("الرجاء إدخال عدد نقاط صحيح");
                    return;
                }
                if (val < 1) {
                    setError("الحد الأدنى للاستبدال هو 1 نقطة");
                    return;
                }
                if (val % 0.25 !== 0) {
                    setError("عدد النقاط يجب أن يكون مضاعفاً لـ 0.25");
                    return;
                }
                if (val > customer.pointsBalance) {
                    setError("رصيد العميل غير كافٍ");
                    return;
                }
                setShowConfirmation(true);
            } else {
                const price = parseFloat(amount);
                if (!price || price <= 0) {
                    setError("الرجاء إدخال سعر المنتج المرتجع");
                    return;
                }
                const calculatedPoints = price * 0.01;
                setPoints(calculatedPoints.toFixed(2));
                setShowConfirmation(true);
            }
        }
    };

    const submittingRef = useRef(false);
    const lastSubmitAtRef = useRef(0);
    const handleConfirm = useCallback(() => {
        console.log("[TransactionForm] handleConfirm fired", {
            submittingRef: submittingRef.current,
            isPending: earnMutation.isPending || redeemMutation.isPending,
            isOnline,
            navigatorOnLine: typeof navigator !== "undefined" ? navigator.onLine : null,
            transactionType,
        });
        // Layer 1: synchronous useRef guard. The auto-confirm timer (60s
        // useEffect) and the "Confirm Now" button click can both invoke
        // handleConfirm within the same render cycle, before isPending flips.
        if (submittingRef.current) {
            console.log("[TransactionForm] BLOCKED by Layer 1 (submittingRef)");
            return;
        }
        // Layer 2: 2-second cooldown. The useEffect that resets submittingRef
        // runs whenever showConfirmation changes, which means a user
        // re-opening the dialog after a failed submit (clicking "متابعة"
        // again to retry) gets a fresh false ref. Without a cooldown, the
        // retry submits again, queues another record, and the loop
        // produces N records in N seconds.
        const now = Date.now();
        if (now - lastSubmitAtRef.current < 2000) {
            console.warn(
                "[TransactionForm] Submit blocked: 2s cooldown after previous submit",
                { lastSubmitAt: lastSubmitAtRef.current, now }
            );
            return;
        }
        // Layer 3: TanStack Query isPending check.
        if (earnMutation.isPending || redeemMutation.isPending) {
            console.log("[TransactionForm] BLOCKED by Layer 3 (isPending)");
            return;
        }
        submittingRef.current = true;
        lastSubmitAtRef.current = now;

        if (transactionType === "EARN") {
            earnMutation.mutate({ barcode: customer.barcode, amount: parseFloat(amount) });
        } else {
            let pointsToDeduct = 0;
            if (redeemMode === "direct") {
                pointsToDeduct = parseFloat(points);
            } else {
                pointsToDeduct = parseFloat(points);
            }
            redeemMutation.mutate({ barcode: customer.barcode, points_to_redeem: pointsToDeduct });
        }
    }, [transactionType, amount, points, customer.barcode, redeemMode, earnMutation, redeemMutation]);

    // Reset the in-flight ref (Layer 1 only) when the dialog closes so the
    // next legitimate submit can proceed. The Layer 2 timestamp cooldown is
    // NOT reset here — it persists across dialog open/close cycles.
    useEffect(() => {
        if (!showConfirmation) submittingRef.current = false;
    }, [showConfirmation]);

    const isSubmitting = earnMutation.isPending || redeemMutation.isPending;
    const handleAmountChange = useCallback((e: any) => setAmount(e.target.value), []);
    const handlePointsChange = useCallback((e: any) => setPoints(e.target.value), []);

    return (
        <Card
            variant="outlined"
            sx={{
                p: 3,
                borderRadius: 3,
                border: '2px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.3s',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
            }}
        >
            {!hideTypeSelector && (
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <Button
                        variant={transactionType === "EARN" ? "contained" : "outlined"}
                        startIcon={<AddCircleOutline />}
                        onClick={() => setTransactionType("EARN")}
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                        }}
                    >
                        إضافة فاتورة
                    </Button>
                    <Button
                        variant={transactionType === "REDEEM_DIRECT" ? "contained" : "outlined"}
                        startIcon={<RemoveCircleOutline />}
                        onClick={() => setTransactionType("REDEEM_DIRECT")}
                        color="error"
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                        }}
                    >
                        خصم نقاط
                    </Button>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>خطأ</AlertTitle>
                    {error}
                </Alert>
            )}

            {successMessage && redirectTimer !== null && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <AlertTitle>نجحت العملية</AlertTitle>
                            <Typography variant="body2">{successMessage}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                العودة بعد {redirectTimer}ث
                            </Typography>
                            <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => navigate("/")}
                            >
                                العودة الآن
                            </Button>
                        </Box>
                    </Box>
                </Alert>
            )}

            {!showConfirmation ? (
                <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {transactionType === "EARN" ? (
                        <TextField
                            label="مبلغ الفاتورة"
                            type="number"
                            value={amount}
                            onChange={handleAmountChange}
                            required
                            InputProps={{
                                startAdornment: <AttachMoney />,
                                endAdornment: <Typography variant="body2">ج.م</Typography>,
                                inputProps: { step: 0.01, min: 0.01, dir: 'ltr' },
                            }}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                <Button
                                    variant={redeemMode === "direct" ? "contained" : "outlined"}
                                    startIcon={<ArrowForward />}
                                    onClick={() => setRedeemMode("direct")}
                                >
                                    استبدال مباشر
                                </Button>
                                <Button
                                    variant={redeemMode === "return" ? "contained" : "outlined"}
                                    startIcon={<ArrowBack />}
                                    onClick={() => setRedeemMode("return")}
                                >
                                    إرجاع منتج
                                </Button>
                            </Box>
                            <Divider />
                            {redeemMode === "direct" ? (
                                <TextField
                                    label="النقاط المراد خصمها"
                                    type="number"
                                    value={points}
                                    onChange={handlePointsChange}
                                    required
                                    error={parseFloat(points) > 0 && parseFloat(points) < 1}
                                    helperText={
                                        parseFloat(points) > 0 && parseFloat(points) < 1
                                            ? "يجب أن يكون مضاعفاً لـ 0.25"
                                            : undefined
                                    }
                                    InputProps={{
                                        startAdornment: <Star />, // icon
                                        endAdornment: <Typography variant="body2">نقطة</Typography>,
                                        inputProps: { step: 0.25, min: 0.25 },
                                    }}
                                    fullWidth
                                />
                            ) : (
                                <TextField
                                    label="سعر المنتج المرتجع"
                                    type="number"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    required
                                    helperText="سيتم حساب النقاط المخصومة تلقائياً"
                                    InputProps={{
                                        startAdornment: <AttachMoney />, // icon
                                        endAdornment: <Typography variant="body2">ج.م</Typography>,
                                        inputProps: { step: 0.01, min: 0.01 },
                                    }}
                                    fullWidth
                                />
                            )}
                        </Box>
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        color={transactionType === "REDEEM_DIRECT" ? "error" : "primary"}
                        disabled={isSubmitting}
                        sx={{
                            height: "3.5rem",
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none',
                        }}
                    >
                        {transactionType === "EARN" ? "متابعة (إضافة)" : "متابعة (خصم)"}
                    </Button>
                </Box>
            ) : (
                <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
                    <DialogTitle>
                        تأكيد العملية
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            سيتم التأكيد تلقائياً بعد {confirmationTimer} ثانية
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body1" gutterBottom>
                            <strong>نوع العملية:</strong>{" "}
                            {transactionType === "EARN"
                                ? "إضافة نقاط (شراء)"
                                : redeemMode === "direct"
                                    ? "استبدال نقاط"
                                    : "إرجاع منتج"}
                        </Typography>
                        {transactionType === "EARN" ? (
                            <Typography variant="h6">مبلغ الفاتورة: {amount} جنيه</Typography>
                        ) : (
                            <Typography variant="h6">النقاط المخصومة: {points}</Typography>
                        )}
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <Typography variant="caption" color="info.main">
                                ⏱️ تأكيد تلقائي: {confirmationTimer} ثانية
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowConfirmation(false)} disabled={isSubmitting}>
                            إلغاء
                        </Button>
                        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isSubmitting}>
                            تأكيد الآن
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Card>
    );
}
